import { DeleteOutlined } from "@ant-design/icons";
import { Button, Form, Input, Modal, Popconfirm, Select, message } from "antd";
import axios from "axios";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { AiOutlineClose } from "react-icons/ai";
import { useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { serverUrl } from "../../nestserver";
import { selectUser } from "../../redux/slices/auth.slice";
import MessageReadStatus from "../readreceipts/MessageReadStatus";
import DiaryComposer from "./DiaryComposer";
import DiaryMedia from "./DiaryMedia";

const ALL_SUBJECTS = "all-subjects";

const authHeaders = () => ({
  Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
});
const get = async (url) =>
  (await axios.get(serverUrl + url, { headers: authHeaders() })).data;

const classIdOf = (student) => student?.class?._id || student?.class_id;
const fullName = (person) =>
  person ? `${person.first_name ?? ""} ${person.last_name ?? ""}`.trim() : "";

const classesUrlFor = (role, isSenior) => {
  if (role === "admin") return "/classes";
  return isSenior ? "/teachers/classes" : "/teachers/instructor-classes";
};

/**
 * Diary feed for one role.
 *  - role: "admin" | "instructor" | "parent" | "student"
 *  - mode: "senior" (class + subject feeds) | "junior" (class feed only)
 * Admin and instructor pick a class; a parent picks a child; a student uses
 * their own class. Only instructors post. Admins and instructors can delete;
 * parents and students mark entries read.
 */
const DiaryBoard = ({ role, mode }) => {
  const isSenior = mode === "senior";
  const isStaff = role === "admin" || role === "instructor";
  const canPost = role === "instructor";

  const user = useSelector(selectUser);
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const messagesRef = useRef(null);
  // Class (staff) or child (parent) chosen in the left pane.
  const [selectedId, setSelectedId] = useState();
  const [scope, setScope] = useState(null);
  const [imageSrc, setImageSrc] = useState("");
  const [videoSrc, setVideoSrc] = useState(null);

  const classes = useQuery(
    ["diary-classes", role, mode],
    () => get(classesUrlFor(role, isSenior)),
    { enabled: isStaff },
  );
  const students = useQuery(
    "students/by-parent",
    async () => (await get("/students/by-parent/")).result,
    { enabled: role === "parent" },
  );

  useEffect(() => {
    if (role === "parent" && students.isSuccess && students.data?.length > 0) {
      const first = students.data[0]._id;
      setSelectedId(first);
      form.setFieldsValue({ class: first });
    }
  }, [role, students.isSuccess, students.data, form]);

  // Whose subjects to load, and which class the diary belongs to.
  const ownerId = role === "student" ? user?._id : selectedId;
  let diaryClassId;
  if (isStaff) diaryClassId = selectedId;
  else if (role === "parent")
    diaryClassId = classIdOf(students.data?.find((s) => s._id === selectedId));
  else diaryClassId = classIdOf(user?.student);

  // Junior schools have no subjects: the class feed opens straight away.
  const activeScope = isSenior ? scope : diaryClassId ? ALL_SUBJECTS : null;

  const subjects = useQuery(
    ["diary-subjects", role, ownerId, diaryClassId],
    async () => {
      let list;
      if (role === "admin") {
        list = (await get(`/classes/${ownerId}/subjects`))?.result?.data
          ?.subjects;
      } else if (role === "instructor") {
        list = (await get(`/classes/${ownerId}`))?.subjects;
      } else {
        list = (await get(`/students/${ownerId}/subject-teacher/`))?.result
          ?.subjects;
      }
      list = list || [];
      if (isStaff) return { subjects: list, classUnread: 0 };

      // Unread badges for parents and students.
      const unreadOf = async (url) => {
        try {
          return (await get(url))?.result?.unread_messages_count || 0;
        } catch (error) {
          return 0;
        }
      };
      return {
        subjects: await Promise.all(
          list.map(async (subject) => ({
            ...subject,
            unread: await unreadOf(`/diary/subject/${subject._id}`),
          })),
        ),
        classUnread: diaryClassId
          ? await unreadOf(`/diary/class/${diaryClassId}`)
          : 0,
      };
    },
    { enabled: isSenior && !!ownerId },
  );

  let entriesUrl = null;
  if (activeScope === ALL_SUBJECTS) {
    if (diaryClassId) entriesUrl = `/diary/class/${diaryClassId}`;
  } else if (activeScope) {
    entriesUrl = `/diary/subject/${activeScope}`;
  }

  const entries = useQuery(["diary", entriesUrl], () => get(entriesUrl), {
    enabled: !!entriesUrl,
    onSuccess: () => {
      if (isStaff) return;
      axios
        .patch(`${serverUrl}${entriesUrl}/read`, null, {
          headers: authHeaders(),
        })
        .then(() => queryClient.invalidateQueries("diary-subjects"))
        .catch((error) => console.error("Error marking diary as read:", error));
    },
  });

  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [entries.data]);

  const handleSend = async ({ message: text, media }) => {
    try {
      const res = await axios.post(
        `${serverUrl}/diary`,
        {
          ...(activeScope === ALL_SUBJECTS
            ? { class_id: diaryClassId }
            : { subject_id: activeScope }),
          ...(text && { message: text }),
          media,
        },
        { headers: authHeaders() },
      );
      message.success(res?.data?.result?.message);
      queryClient.invalidateQueries(["diary", entriesUrl]);
      return true;
    } catch (err) {
      message.error(
        err?.response?.data?.error?.errors ||
          err?.response?.data?.message ||
          "Failed to send diary entry",
      );
      return false;
    }
  };

  const handleDelete = (entryId) => {
    axios
      .delete(`${serverUrl}/diary/${entryId}`, { headers: authHeaders() })
      .then(() => {
        queryClient.invalidateQueries(["diary", entriesUrl]);
        message.success("Diary entry deleted");
      })
      .catch((err) => console.error(err));
  };

  const handleClassChange = (value) => {
    setSelectedId(value);
    setScope(null);
  };

  const resetSelection = () => {
    setScope(null);
    if (role !== "parent") setSelectedId(null);
    if (isStaff) form.resetFields(["class"]);
  };

  const subjectCard = (id, label, unread) => (
    <div className="pt-2 text-base cursor-pointer" key={id}>
      <div
        onClick={() => setScope(id)}
        className={`cursor-pointer ${
          scope === id ? "active-message" : "message-card"
        }`}
      >
        <div className="flex justify-between items-center">
          <div>{label}</div>
          {unread > 0 && (
            <div className="rounded-full border flex justify-center items-center unread-message">
              <p>{unread}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderSelector = () => {
    if (isStaff) {
      return (
        <Form.Item name="class" label="Class" className="w-30">
          <Select
            showSearch
            placeholder="Select Class"
            className="w-full bg-gray-300"
            style={{ border: "6px", backgroundColor: "#EBEBEB" }}
            getPopupContainer={(trigger) => trigger.parentNode}
            onChange={handleClassChange}
            options={(classes.data || []).map((c) => ({
              value: c._id,
              label: c.name,
            }))}
          />
        </Form.Item>
      );
    }
    if (role === "parent") {
      return (
        <Form.Item name="class" label="Student Name" className="w-30">
          {students.data?.length === 1 ? (
            <div className="border p-2 rounded">
              {fullName(students.data[0])}
            </div>
          ) : (
            <Select
              showSearch
              placeholder="Select student"
              className="w-full bg-gray-300"
              style={{ border: "6px", backgroundColor: "#EBEBEB" }}
              getPopupContainer={(trigger) => trigger.parentNode}
              onChange={handleClassChange}
              options={(students.data || []).map((s) => ({
                value: s._id,
                label: fullName(s),
              }))}
            />
          )}
        </Form.Item>
      );
    }
    return (
      <Form.Item label="Student Name" className="w-30">
        <Input readOnly value={fullName(user?.student)} />
      </Form.Item>
    );
  };

  const feed = entries.data?.result?.messages;

  return (
    <div>
      <div className="p-8">
        <div
          style={{ borderRadius: "10px", color: "#616161" }}
          className=" bg-white drop-shadow-md p-8 w-full screen-height "
        >
          <div className="flex gap-4 min-height">
            <div style={{ width: "30%" }} className="message-left-part">
              <Form form={form} layout="vertical" autoComplete="off">
                {renderSelector()}
                {isSenior && (
                  <Form.Item label="Subject" className="w-30">
                    <div
                      style={{
                        maxHeight: "calc(100vh - 320px)",
                        overflowY: "auto",
                      }}
                    >
                      {!!diaryClassId &&
                        !!subjects.data &&
                        subjectCard(
                          ALL_SUBJECTS,
                          "All Subjects",
                          subjects.data.classUnread,
                        )}
                      {subjects.data?.subjects?.map((subject) =>
                        subjectCard(subject._id, subject.name, subject.unread),
                      )}
                    </div>
                  </Form.Item>
                )}
              </Form>
            </div>
            <div style={{ width: "70%" }} className="instructor-broadcast">
              {activeScope ? (
                <>
                  <div className="flex justify-between items-center pb-4">
                    <h1 className="pb-2 text-base font-semibold">Diary</h1>
                    {(isStaff || isSenior) && (
                      <AiOutlineClose
                        size={20}
                        onClick={resetSelection}
                        className="cursor-pointer"
                      />
                    )}
                  </div>
                  <div
                    className={`${
                      canPost
                        ? "message-chat-content"
                        : "message-chat-content-class"
                    } pt-8`}
                    style={{ width: "70%", marginLeft: "auto" }}
                    ref={messagesRef}
                  >
                    {feed?.map((entry) => (
                      <div className="pt-4" key={entry._id}>
                        <div className="flex items-center gap-4 pb-4 justify-end">
                          <div className="flex flex-col gap-1">
                            <span>
                              {fullName(
                                entry.sender?.teacher || entry.sender?.admin,
                              )}
                            </span>
                            <span className="flex items-center gap-2">
                              {moment(entry.createdAt).format(
                                "DD-MM-YYYY hh:mm A",
                              )}
                              {isStaff && (
                                <MessageReadStatus
                                  message={entry}
                                  readInfoPath={`/diary/${entry._id}/read-info`}
                                />
                              )}
                            </span>
                          </div>
                          <div
                            className={
                              isStaff ? "message-box" : "message-box-other"
                            }
                          >
                            {entry.media?.map((item, index) => (
                              <DiaryMedia
                                key={item.id || index}
                                item={item}
                                onImageClick={setImageSrc}
                                onVideoClick={setVideoSrc}
                              />
                            ))}
                            {entry.message}
                          </div>
                          {isStaff && (
                            <Popconfirm
                              title="Are you sure to delete?"
                              onConfirm={() => handleDelete(entry._id)}
                              okText="Yes"
                              cancelText="No"
                            >
                              <Button
                                danger
                                shape="circle"
                                icon={
                                  <DeleteOutlined
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  />
                                }
                              />
                            </Popconfirm>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {canPost && <DiaryComposer onSend={handleSend} />}
                </>
              ) : (
                <div className="flex justify-center items-center h-full w-full">
                  No diary entries found
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={!!imageSrc}
        footer={null}
        onCancel={() => setImageSrc("")}
        centered
      >
        <img src={imageSrc} alt="Preview" style={{ width: "100%", marginTop: "20px" }} />
      </Modal>
      <Modal
        open={!!videoSrc}
        footer={null}
        onCancel={() => setVideoSrc(null)}
        width={800}
        centered
        destroyOnClose
        styles={{ body: { padding: 0, backgroundColor: "black" } }}
      >
        {videoSrc && (
          <video
            controls
            autoPlay
            style={{ width: "100%", maxHeight: "80vh", display: "block" }}
          >
            <source src={videoSrc} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        )}
      </Modal>
    </div>
  );
};

export default DiaryBoard;
